import { Prescription, RenderMedia, RenderPlatform, RxScannerVersion } from "@fusion-advantage/backcompat"
import { DrugScannerResult, mergeManualDrugs, prescriptionsFromScanner } from "@fusion-advantage/formlib"
import { useFormDispatch, useFormSelector } from "../../../redux/formStoreHooks"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useRxScannerPlugin } from "../../../lib/useNativePlugin"
import _ from 'lodash'
import L3MedicationReconciliationTableActions from "../l3/L3MedicationReconciliationTableActions"
import { setDrugProducts, setPrescriptions } from "../../../redux/drugSlice"
import { selectAllIcd10s } from "../../../redux/selectMedicationReconciliation"
import { useFormAppRelay } from "../../../lib/relay/FormAppRelay"
import L3MedReconManualEntryModal from "../l3/L3MedReconManualEntryModal"
import { L1MedicationReconciliationHelpDialog } from "../l1/L1MedicationReconcilliation"

const L4MedicationReconciliationTableActions = () => {
	const [showDrugSearch, setShowDrugSearch] = useState(false)
	const [showHelp, setShowHelp] = useState(false)
	const platform = useFormSelector(state => state.render.platform)
	const dispatch = useFormDispatch()
	const models = useFormSelector(state => state.drug.models)
	const drugList = useFormSelector(state => state.drug.drugList)
	const icds = useFormSelector(selectAllIcd10s)
	const media = useFormSelector(state => state.render.media)
	const { queryDrugProducts } = useFormAppRelay()

	const priorIds = useMemo(() => {
		return models
			.filter(x => x.IsPrior)
			.map(p => p.Product.ID)
	}, [models])
	
	const handleChange = useCallback((newModels: Prescription[]) => {
		dispatch(setPrescriptions(newModels))
	}, [dispatch])

	const handleScanReceive = useCallback((products: DrugScannerResult[]) => {
		handleChange(prescriptionsFromScanner(models, products))
	}, [handleChange, models])
		
	const { showRxScanner } = useRxScannerPlugin({
		onReceive: handleScanReceive,
	})

	const handleScanClick = useCallback(() => {
		if(platform === RenderPlatform.Native) {
			showRxScanner({
				version: RxScannerVersion.V2Simplified,
				models: models,
				assessmentSupportedIcd10s: icds.supportedIcd10s,
				priorIcd10s: icds.priorIcd10s,
			})
		} else {
			alert('Sorry, this feature is only available on the iPad app.')
		}
	}, [showRxScanner, models, icds, platform])

	const handleSaveManualDrugs = useCallback((newModels: Prescription[]) => {
		const mergedDrugs = mergeManualDrugs(models, newModels)
		handleChange(mergedDrugs)
		setShowDrugSearch(false)
	}, [models, handleChange])

	useEffect(() => {
		const fetchDrugListAsync = async () => {
			const { products } = await queryDrugProducts()

			dispatch(setDrugProducts(products))
		}

		fetchDrugListAsync()
	}, [])

	return (
		<>
		{
			media === RenderMedia.Print ? (
				null
			) : (
				<>
					<L1MedicationReconciliationHelpDialog 
						isOpen={showHelp}
						onClose={() => setShowHelp(false)}
					/>
					<L3MedReconManualEntryModal
						models={models}
						drugsList={drugList}
						onSave={handleSaveManualDrugs}
						onClose={() => setShowDrugSearch(false)}
						show={showDrugSearch}
						priorIds={priorIds}
					/>
					<L3MedicationReconciliationTableActions
						onScannerClick={handleScanClick}
						onManualEntryClick={() => setShowDrugSearch(true)}
						onHelpClick={() => setShowHelp(true)}
					/>
				</>
			)
		}
		</>
	)
}

export default L4MedicationReconciliationTableActions